import {forwardRef, useEffect, useState} from 'react'
import {Avatar, Group, Select, Text} from '@mantine/core'
import {Search} from 'tabler-icons-react'
import {useNavigate} from 'react-router-dom'
import {useDebouncedState} from '@mantine/hooks'
import {useUserSearchLazyQuery, useUserSearchQuery} from "../generated/graphql";

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
    image: string
    label: string
    description: string
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
    ({image, label, description, ...others}: ItemProps, ref) => (
        <div ref={ref} {...others}>
            <Group>
                <Avatar src={image}/>

                <div>
                    <Text size="sm">{label}</Text>
                    <Text size="xs" opacity={0.65}>
                        {description}
                    </Text>
                </div>
            </Group>
        </div>
    )
)

/**
 * Props for the ProfileSearch component.
 * @property setSidenavOpened - Optional callback to close the sidenav after navigation.
 * @property className - CSS class(es) to apply to the search container.
 * @property style - Inline styles to apply to the search container.
 */
interface ProfileSearchProps {
    setSidenavOpened?: (value: boolean) => void;
    className?: string;
    style?: React.CSSProperties;
}

export default function ProfileSearch(props: ProfileSearchProps) {
    const navigate = useNavigate()
    const [input, setInput] = useDebouncedState('', 200)
    const [userList, setUserList] = useState<string[]>([])
    const [getUsers, {loading, error, data}] = useUserSearchLazyQuery();

    useEffect(() => {
        getUsers({variables: {input: input}}).then(
            data=>{
                setUserList(data.data?.users?.items?.map(x=>x.name) ?? []);
            }
        )
    }, [input]);


    return (
        <Select
            className={props.className ? `mr-4 ${props.className}` : 'mr-4'}
            style={props.style}
            size="md"
            leftSection={<Search/>}
            placeholder="Search for a player"
            onSearchChange={(value) => {
                setInput(value ?? '')
            }}
            onChange={(value) => {
                navigate(`/stats/profile/${encodeURIComponent(value ?? '')}`)
                if (props.setSidenavOpened) {
                    props.setSidenavOpened(false)
                }
            }}
            data={userList}
            searchable
            maxDropdownHeight={400}
        />
    )
}
